// Preserves the SPA route across full-page MSAL auth redirects (SUP-02, "app
// forgets state and reloads").
//
// acquireTokenRedirect / loginRedirect are top-level navigations: Azure AD
// sends the browser back to the registered redirectUri (the app origin root),
// so without help the user is dumped on "/" with the route they were working
// in gone. Before starting an interactive redirect, authToken.ts stashes the
// current SPA location here (sessionStorage: per-tab, survives the same-tab
// redirect round-trip, never shared across tabs); after MSAL finishes
// processing the redirect response, main.tsx restores it with
// history.replaceState BEFORE React mounts — the router boots directly on the
// original route with no extra navigation or reload.

export const RETURN_LOCATION_KEY = 'supervision.auth.returnTo';

type LocationLike = Pick<Location, 'pathname' | 'search' | 'hash'>;

/** Stash the current route so it can be restored after an auth redirect. */
export function saveReturnLocation(loc: LocationLike = window.location): void {
  try {
    sessionStorage.setItem(RETURN_LOCATION_KEY, `${loc.pathname}${loc.search}${loc.hash}`);
  } catch {
    // Storage unavailable — losing the route is non-fatal (user lands on "/").
  }
}

/**
 * Read and clear the stashed route (one-shot). Only same-origin absolute paths
 * are trusted — anything else (absolute or protocol-relative URL, e.g. a
 * poisoned stash) is discarded so this can never become an open redirect.
 */
export function consumeReturnLocation(): string | null {
  try {
    const value = sessionStorage.getItem(RETURN_LOCATION_KEY);
    sessionStorage.removeItem(RETURN_LOCATION_KEY);
    if (value && value.startsWith('/') && !value.startsWith('//')) return value;
    return null;
  } catch {
    return null;
  }
}

/**
 * If a route was stashed before the auth redirect, put it back into the
 * address bar (replaceState — no navigation, no reload) so the router mounts
 * on the page the user was actually on. Call after handleRedirectPromise and
 * before rendering the app.
 */
export function restoreReturnLocation(): void {
  const target = consumeReturnLocation();
  if (!target) return;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (target !== current) {
    window.history.replaceState(null, '', target);
  }
}
